import React, { useState, useEffect } from 'react';
import Competitor360Header from './Competitor360Header';
import VehicleDistribution from './VehicleDistribution';
import StationNetworkAnalysis from './StationNetworkAnalysis';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Clock, Filter } from 'lucide-react';

const Competitor360View = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState('All Segments');
  const [selectedStation, setSelectedStation] = useState('All Stations');
  
  // Mock competitor data
  const competitorData = [
    { id: 'COMP001', name: 'Shell Thailand', brand: 'Shell', area: 'Nationwide', stations: '560', marketShare: '24.5%' },
    { id: 'COMP002', name: 'PTT Oil', brand: 'PTT', area: 'Nationwide', stations: '680', marketShare: '28.5%' },
    { id: 'COMP003', name: 'Bangchak Petroleum', brand: 'Bangchak', area: 'Nationwide', stations: '420', marketShare: '18.2%' },
    { id: 'COMP004', name: 'Esso Thailand', brand: 'Esso', area: 'Nationwide', stations: '380', marketShare: '15.8%' },
    { id: 'COMP005', name: 'Caltex Thailand', brand: 'Caltex', area: 'Nationwide', stations: '310', marketShare: '13.0%' },
  ];

  // Mock segment data
  const segmentOptions = ['All Segments', 'Urban', 'Suburban', 'Highway', 'Rural'];
  
  // Mock station data
  const stationOptions = ['All Stations', 'Premium Stations', 'Standard Stations', 'Budget Stations', 'Flagship Stations'];

  // Set the first competitor as selected by default
  useEffect(() => {
    if (competitorData.length > 0 && !selectedCompetitor) {
      setSelectedCompetitor(competitorData[0]);
    }
  }, [competitorData, selectedCompetitor]);

  const handleSelectCompetitor = (competitor) => {
    setSelectedCompetitor(competitor);
  };

  // Filter data based on selected segment and station
  const getFilteredData = (data, type) => {
    if (!data) return [];
    
    // If both filters are set to 'All', return original data
    if (selectedSegment === 'All Segments' && selectedStation === 'All Stations') {
      return data;
    }
    
    // Apply multipliers based on filters to simulate different data for different selections
    let multiplier = 1;
    
    if (selectedSegment !== 'All Segments') {
      // Different segments have different impacts
      switch (selectedSegment) {
        case 'Urban':
          multiplier *= 1.2;
          break;
        case 'Suburban':
          multiplier *= 0.9;
          break;
        case 'Highway':
          multiplier *= 1.5;
          break;
        case 'Rural':
          multiplier *= 0.7;
          break;
        default:
          multiplier *= 1;
      }
    }
    
    if (selectedStation !== 'All Stations') {
      // Different station types have different impacts
      switch (selectedStation) {
        case 'Premium Stations':
          multiplier *= 1.3;
          break;
        case 'Standard Stations':
          multiplier *= 1;
          break;
        case 'Budget Stations':
          multiplier *= 0.8;
          break;
        case 'Flagship Stations':
          multiplier *= 1.6;
          break;
        default:
          multiplier *= 1;
      }
    }
    
    // Apply the multiplier to different data types
    if (type === 'revenue') {
      return data.map(item => ({
        ...item,
        competitor: Math.round(item.competitor * multiplier),
        ourCompany: Math.round(item.ourCompany * multiplier)
      }));
    } else if (type === 'pricing') {
      return data.map(item => ({
        ...item,
        competitor: +(item.competitor * (1 + (multiplier - 1) * 0.1)).toFixed(2),
        ourCompany: +(item.ourCompany * (1 + (multiplier - 1) * 0.1)).toFixed(2)
      }));
    } else if (type === 'differential') {
      return data.map(item => ({
        ...item,
        differential: +(item.differential * multiplier).toFixed(2)
      }));
    } else if (type === 'traffic') {
      return data.map(item => ({
        ...item,
        competitor: Math.round(item.competitor * multiplier),
        ourCompany: Math.round(item.ourCompany * multiplier)
      }));
    } else if (type === 'nonFuel') {
      return data.map(item => ({
        ...item,
        ourScore: +(Math.min(10, item.ourScore * (1 + (multiplier - 1) * 0.2))).toFixed(1),
        competitorScore: +(Math.min(10, item.competitorScore * (1 + (multiplier - 1) * 0.2))).toFixed(1)
      }));
    }
    
    return data;
  };

  // Mock revenue data for charts
  const baseRevenueData = [
    { name: 'Jan', competitor: 110, ourCompany: 120 },
    { name: 'Feb', competitor: 115, ourCompany: 130 },
    { name: 'Mar', competitor: 120, ourCompany: 118 },
    { name: 'Apr', competitor: 125, ourCompany: 130 },
    { name: 'May', competitor: 130, ourCompany: 135 },
    { name: 'Jun', competitor: 135, ourCompany: 140 },
  ];

  // Mock pricing data
  const basePricingData = [
    { name: 'Jan', competitor: 31.65, ourCompany: 31.75 },
    { name: 'Feb', competitor: 31.45, ourCompany: 31.55 },
    { name: 'Mar', competitor: 31.79, ourCompany: 31.89 },
    { name: 'Apr', competitor: 32.10, ourCompany: 32.20 },
    { name: 'May', competitor: 31.95, ourCompany: 32.05 },
    { name: 'Jun', competitor: 31.50, ourCompany: 31.60 },
  ];

  // Mock differential data
  const baseDifferentialData = [
    { name: 'Jan', differential: 0.10 },
    { name: 'Feb', differential: 0.10 },
    { name: 'Mar', differential: 0.10 },
    { name: 'Apr', differential: 0.10 },
    { name: 'May', differential: 0.10 },
    { name: 'Jun', differential: 0.10 },
  ];

  // Mock traffic data
  const baseTrafficData = [
    { day: 'Monday', competitor: 3200, ourCompany: 3500 },
    { day: 'Tuesday', competitor: 3100, ourCompany: 3300 },
    { day: 'Wednesday', competitor: 3400, ourCompany: 3600 },
    { day: 'Thursday', competitor: 3300, ourCompany: 3700 },
    { day: 'Friday', competitor: 3800, ourCompany: 4000 },
    { day: 'Saturday', competitor: 4200, ourCompany: 4100 },
    { day: 'Sunday', competitor: 3600, ourCompany: 3800 },
  ];

  // Mock non-fuel offerings data
  const baseNonFuelData = [
    { category: 'Convenience Store', ourScore: 8.5, competitorScore: 7.8 },
    { category: 'Food & Beverage', ourScore: 9.0, competitorScore: 8.2 },
    { category: 'Car Services', ourScore: 7.5, competitorScore: 7.4 },
    { category: 'Digital Experience', ourScore: 8.0, competitorScore: 8.6 },
  ];

  // Get filtered data based on current selections
  const revenueData = getFilteredData(baseRevenueData, 'revenue');
  const pricingData = getFilteredData(basePricingData, 'pricing');
  const differentialData = getFilteredData(baseDifferentialData, 'differential');
  const trafficData = getFilteredData(baseTrafficData, 'traffic');
  const nonFuelData = getFilteredData(baseNonFuelData, 'nonFuel');

  // Mock promotions data
  const promotionsData = [
    {
      id: 1,
      title: 'Shell V-Power Double Points',
      description: 'Double loyalty points on V-Power premium fuel purchases',
      startDate: '01 May 2023',
      endDate: '31 Jul 2023',
      type: 'Loyalty Program',
      impact: 'Medium Impact'
    },
    {
      id: 2,
      title: 'Shell Go+ App Launch',
      description: 'New mobile app with personalized offers and payment options',
      startDate: '15 Jun 2023',
      endDate: 'Ongoing',
      type: 'Digital',
      impact: 'High Impact'
    },
    {
      id: 3,
      title: 'Breakfast Meal Deal',
      description: 'Discounted coffee and breakfast item with 30L+ fuel purchase before 10am',
      startDate: '01 Apr 2023',
      endDate: '30 Jun 2023',
      type: 'Bundled Offer',
      impact: 'Medium Impact'
    }
  ];

  const renderOverviewTab = () => {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-4 col-span-1 lg:col-span-2">
          <h3 className="text-xl font-medium text-gray-900 mb-4">Competitor Overview</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Market Position</h4>
              
              <div className="mb-4">
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Market Share</span>
                  <span className="text-sm font-medium">{selectedCompetitor?.marketShare}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-purple-600 h-2.5 rounded-full" style={{ width: selectedCompetitor?.marketShare }}></div>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Brand Strength</span>
                  <span className="text-sm font-medium">8.4/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: '84%' }}></div>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Customer Loyalty</span>
                  <span className="text-sm font-medium">7.8/10</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-green-600 h-2.5 rounded-full" style={{ width: '78%' }}></div>
                </div>
              </div>

              <h4 className="text-lg font-medium text-gray-900 mt-6 mb-3">Growth Trends</h4>
              
              <div className="flex items-center justify-between mb-3 text-sm">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-2" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L9 3M9 3H4M9 3V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Station Growth (YoY)</span>
                </div>
                <span className="text-green-600">+4.2%</span>
              </div>
              
              <div className="flex items-center justify-between mb-3 text-sm">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-2" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L9 3M9 3H4M9 3V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Market Share Growth</span>
                </div>
                <span className="text-green-600">+1.3%</span>
              </div>
              
              <div className="flex items-center justify-between mb-3 text-sm">
                <div className="flex items-center">
                  <svg className="w-3 h-3 text-green-500 mr-2" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 9L9 3M9 3H4M9 3V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Volume Growth</span>
                </div>
                <span className="text-green-600">+3.8%</span>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Key Strengths</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-green-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Premium brand perception</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-green-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Strong highway presence</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-green-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Advanced fuel technology marketing</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-green-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Quality food partnerships</span>
                </li>
              </ul>
              
              <h4 className="text-lg font-medium text-gray-900 mt-6 mb-3">Key Weaknesses</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-red-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-red-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Higher price point than competitors</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-red-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-red-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Lower urban density in some regions</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-red-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-red-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Limited loyalty program benefits</span>
                </li>
              </ul>
              
              <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h5 className="font-medium text-gray-900 mb-1 flex items-center">
                  <span className="text-yellow-500 mr-1">⚠</span> 
                  Competitive Alert
                </h5>
                <p className="text-sm text-gray-600">
                  Competitor is opening multiple new retail stations in the rural segment across the network with compelling premium fuel offers which can be a threat to PTTOR
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Comparison (Last 6 Months)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={revenueData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 160]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="ourCompany" name="Our Company" stroke="#8B5CF6" activeDot={{ r: 8 }} />
                <Line type="monotone" dataKey="competitor" name={selectedCompetitor?.brand || 'Competitor'} stroke="#6B7280" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Non-Fuel Offerings Comparison</h3>
          <div className="space-y-6">
            {nonFuelData.map((item) => (
              <div key={item.category}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">{item.category}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-500">Our Score:</span>
                      <span className="text-xs font-medium">{item.ourScore}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${(item.ourScore / 10) * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-500">Competitor:</span>
                      <span className="text-xs font-medium">{item.competitorScore}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(item.competitorScore / 10) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 col-span-1 lg:col-span-2">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Promotional Activity</h3>
          <div className="space-y-4">
            {promotionsData.map((promo) => (
              <div key={promo.id} className="flex items-start p-3 border-b border-gray-100">
                <div className="flex-shrink-0 bg-blue-50 p-2 rounded-full mr-3">
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <h4 className="font-medium text-gray-900">{promo.title}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      promo.impact === 'High Impact' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {promo.impact}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{promo.description}</p>
                  <div className="flex mt-2 text-xs text-gray-500">
                    <span className="mr-3">{promo.startDate} - {promo.endDate}</span>
                    <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded">{promo.type}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4 col-span-1 lg:col-span-2">
          <VehicleDistribution />
        </div>
      </div>
    );
  };

  const renderPricingStrategyTab = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-medium text-gray-900">Price Analysis</h3>
            <div className="flex space-x-2">
              <button className="bg-purple-600 text-white px-3 py-1 rounded-md text-sm font-medium">Diesel</button>
              <button className="bg-white text-gray-700 border border-gray-300 px-3 py-1 rounded-md text-sm font-medium">Gasohol 95</button>
              <button className="bg-white text-gray-700 border border-gray-300 px-3 py-1 rounded-md text-sm font-medium">3M</button>
              <button className="bg-white text-gray-700 border border-gray-300 px-3 py-1 rounded-md text-sm font-medium">6M</button>
              <button className="bg-white text-gray-700 border border-gray-300 px-3 py-1 rounded-md text-sm font-medium">1Y</button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="text-lg font-medium text-gray-800 mb-3">Diesel Price Trend</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={pricingData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[31.4, 32.2]} />
                    <Tooltip 
                      formatter={(value, name) => [`${value} ฿`, name === 'competitor' ? selectedCompetitor?.brand : 'Our Company']}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Legend 
                      formatter={(value) => value === 'competitor' ? selectedCompetitor?.brand : 'Our Company'}
                    />
                    <Line type="monotone" dataKey="ourCompany" name="Our Company" stroke="#8B5CF6" activeDot={{ r: 8 }} />
                    <Line type="monotone" dataKey="competitor" name="competitor" stroke="#6B7280" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-medium text-gray-800 mb-3">Price Differential Analysis (Our Price - Competitor)</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={differentialData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 0.1]} tickFormatter={(value) => value.toFixed(2)} />
                    <Tooltip formatter={(value) => [`${value.toFixed(2)} ฿`, 'Price Differential']} />
                    <Bar dataKey="differential" name="Price Differential" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xl font-medium text-gray-900 mb-4">Pricing Strategy Analysis</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-3">Price Leadership</h4>
              <p className="text-sm text-gray-600 mb-4">
                Shell typically acts as a price leader, often initiating price increases before other major brands. They maintain a premium position and rarely engage in price wars.
              </p>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-500">Price Leader Score:</span>
                <span className="text-sm font-medium text-red-600">8/10</span>
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-3">Response Time</h4>
              <p className="text-sm text-gray-600 mb-4">
                When we adjust prices, Shell responds conservatively, typically waiting to confirm market trends before following with their own adjustments.
              </p>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-500">Average Response Time:</span>
                <span className="text-sm font-medium text-gray-900">2.8 days</span>
              </div>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-3">Price Positioning</h4>
              <p className="text-sm text-gray-600 mb-4">
                Consistently positions as a premium offering, maintaining prices 0.10-0.40฿ higher than market average, with emphasis on fuel quality messaging.
              </p>
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-gray-500">Current Position:</span>
                <span className="text-sm font-medium text-gray-900">Premium (+0.25฿)</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xl font-medium text-gray-900 mb-4">Strategic Price Recommendations</h3>
          
          <div className="p-4 border border-gray-200 rounded-lg">
            <h4 className="text-lg font-medium text-purple-700 mb-2">Maintain Strategic Price Differential</h4>
            <p className="text-gray-600">
              Continue to maintain a 0.15฿/L differential for regular fuels while ensuring premium fuels are priced competitively to prevent customer switching.
            </p>
            <div className="mt-4">
              <button className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium mr-3">Apply Recommendation</button>
              <button className="bg-white text-purple-700 border border-purple-300 px-4 py-2 rounded-md text-sm font-medium">Simulate Impact</button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const renderTrafficAnalysisTab = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xl font-medium text-gray-900 mb-4">Daily Traffic Analysis</h3>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={trafficData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="ourCompany" name="Our Stations" fill="#8B5CF6" />
                <Bar dataKey="competitor" name={selectedCompetitor?.brand || 'Competitor'} fill="#6B7280" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-3">Traffic Insights</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-purple-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-purple-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Weekends show 18% higher traffic than weekdays</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-purple-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-purple-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Peak hours are 7-9 AM and 5-7 PM on weekdays</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-purple-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-purple-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Our stations outperform competitor by avg. 7.3%</span>
                </li>
              </ul>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg">
              <h4 className="text-lg font-medium text-gray-800 mb-3">Traffic Opportunities</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-green-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Targeted weekday morning promotions can boost traffic by ~12%</span>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-green-500 rounded-full flex items-center justify-center mt-0.5">
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="ml-2 text-gray-600">Tuesday has lowest traffic - opportunity for loyalty program focus</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-xl font-medium text-gray-900 mb-4">Traffic Conversion Analysis</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="text-center mb-4">
                <span className="text-3xl font-bold text-purple-700">76%</span>
                <h4 className="text-gray-600 text-sm">Fuel-Only Customers</h4>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Competitor: 81%
              </p>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="text-center mb-4">
                <span className="text-3xl font-bold text-purple-700">18%</span>
                <h4 className="text-gray-600 text-sm">Fuel + Convenience</h4>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Competitor: 15%
              </p>
            </div>
            
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="text-center mb-4">
                <span className="text-3xl font-bold text-purple-700">6%</span>
                <h4 className="text-gray-600 text-sm">Convenience Only</h4>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Competitor: 4%
              </p>
            </div>
          </div>
          
          <div className="p-4 border border-gray-200 rounded-lg mt-6">
            <h4 className="text-lg font-medium text-purple-700 mb-2">Traffic Conversion Recommendation</h4>
            <p className="text-gray-600">
              Implement fuel + food bundle promotions during weekday lunch hours (11am-1pm) to increase 
              Fuel + Convenience conversion rate to target of 22% (currently 18%).
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'pricing':
        return renderPricingStrategyTab();
      case 'network':
        return <StationNetworkAnalysis competitor={selectedCompetitor} />;
      case 'traffic':
        return renderTrafficAnalysisTab();
      default:
        return <div>Select a tab to view content</div>;
    }
  };
  
  const renderFilters = () => {
    return (
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row md:items-center mb-4">
          <div className="flex items-center mb-2 md:mb-0 mr-4">
            <Filter className="h-5 w-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
            <div>
              <label htmlFor="segment-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Segment
              </label>
              <select
                id="segment-filter"
                value={selectedSegment}
                onChange={(e) => setSelectedSegment(e.target.value)}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-sm"
              >
                {segmentOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="station-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Stations
              </label>
              <select
                id="station-filter"
                value={selectedStation}
                onChange={(e) => setSelectedStation(e.target.value)}
                className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 text-sm"
              >
                {stationOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {(selectedSegment !== 'All Segments' || selectedStation !== 'All Stations') && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {selectedSegment !== 'All Segments' && (
              <div className="bg-purple-100 text-purple-800 text-xs px-3 py-1 rounded-full flex items-center">
                Segment: {selectedSegment}
                <button 
                  onClick={() => setSelectedSegment('All Segments')}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </div>
            )}
            
            {selectedStation !== 'All Stations' && (
              <div className="bg-purple-100 text-purple-800 text-xs px-3 py-1 rounded-full flex items-center">
                Station: {selectedStation}
                <button 
                  onClick={() => setSelectedStation('All Stations')}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setSelectedSegment('All Segments');
                setSelectedStation('All Stations');
              }}
              className="text-xs text-purple-600 hover:text-purple-800 underline"
            >
              Clear All
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <Competitor360Header />
      
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Competitor Intelligence</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Competitor ID
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Competitor Name
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Brand
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Area
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stations
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Market Share
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {competitorData.map((competitor) => (
                  <tr 
                    key={competitor.id} 
                    className={`cursor-pointer hover:bg-purple-50 ${selectedCompetitor?.id === competitor.id ? 'bg-purple-100' : ''}`}
                    onClick={() => handleSelectCompetitor(competitor)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {competitor.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competitor.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competitor.brand}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competitor.area}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competitor.stations}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {competitor.marketShare}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                      <button className="font-medium hover:text-purple-800">
                        Analyze
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {selectedCompetitor && (
        <div>
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedCompetitor.name} Competitive Analysis
              </h2>
              <p className="text-gray-600">
                Brand: {selectedCompetitor.brand} • Area: {selectedCompetitor.area} • 
                Stations: {selectedCompetitor.stations}
              </p>
            </div>
            <div className="flex space-x-2">
              <button className="bg-white text-purple-700 border border-purple-300 rounded px-4 py-2 hover:bg-purple-50">
                Export Data
              </button>
              <button className="bg-purple-600 text-white rounded px-4 py-2 hover:bg-purple-700">
                Set Alerts
              </button>
            </div>
          </div>
          
          {renderFilters()}
          
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-purple-600 text-purple-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'pricing'
                    ? 'border-purple-600 text-purple-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pricing Strategy
              </button>
              <button
                onClick={() => setActiveTab('network')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'network'
                    ? 'border-purple-600 text-purple-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Station Network
              </button>
              <button
                onClick={() => setActiveTab('traffic')}
                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
                  activeTab === 'traffic'
                    ? 'border-purple-600 text-purple-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Traffic Analysis
              </button>
            </nav>
          </div>
          
          {renderTabContent()}
        </div>
      )}
    </div>
  );
};

export default Competitor360View;
